import { type ApolloClient, ApolloError, useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useSyncExternalStore } from 'react';

import {
	AppendTranscriptionDocument,
	type AppendTranscriptionMutation,
	type AppendTranscriptionMutationVariables,
	RegenerateTranscriptionSummaryDocument,
	type RegenerateTranscriptionSummaryMutation,
	type RegenerateTranscriptionSummaryMutationVariables,
	StartStreamingTranscriptionDocument,
	type StartStreamingTranscriptionMutation,
	type StartStreamingTranscriptionMutationVariables,
	type TranscriptionLanguage,
	type TranscriptionTargetType,
} from '@treams/treams-network';

import { type AudioSource, createAudioSource } from './streaming/audioSource';
import {
	type QueuedChunk,
	type StoredSession,
	loadStoredSessions,
	removeStoredSession,
	saveStoredSession,
} from './streaming/textQueue';

const STREAMING_URL = 'wss://streaming.eu.assemblyai.com/v3/ws';
const SPEECH_MODEL = 'universal-3-6-pro';
/** Live calls are always two people. A hard cap so a third voice is not a new speaker. */
const MAX_SPEAKERS = '2';
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000];
const STEADY_RETRY_DELAY_MS = 15_000;
const BUSY_RETRY_DELAY_MS = 30_000;
const TERMINATION_TIMEOUT_MS = 5_000;
/** AssemblyAI ends a session after 3 hours; move to a new one before that. */
const SESSION_ROTATION_MS = (2 * 60 + 50) * 60 * 1_000;
const MAX_BUFFERED_BYTES = 1_000_000;
const COUNTDOWN_SECONDS = 3;
const MAX_BATCH_CHUNKS = 50;
/** Bars kept for the banner waveform; older ones scroll out of view anyway. */
const MAX_BARS = 40;
/** Server errors in a row before a start, save or discard gives up. */
const MAX_SERVER_ERRORS = 3;
const PERSIST_EVERY_SECONDS = 5;
const TRANSCRIPT_BREAK = '<BREAK>';
/** Input peak (0–1) below which a second counts as quiet. */
const SILENCE_LEVEL = 0.02;
/** Quiet seconds in a row before the live transcript shows "silence". */
const SILENCE_SECONDS = 3;
export const LOCK_PREFIX = 'treams-streaming-transcription:';

export type AssemblyStatus =
	| 'idle'
	| 'connecting'
	| 'live'
	| 'reconnecting'
	| 'ending'
	| 'closed'
	| 'failed';

export type SyncStatus = 'idle' | 'pending' | 'syncing' | 'synced' | 'failed';

export type StreamingPhase =
	| 'idle'
	| 'starting'
	| 'countdown'
	| 'recording'
	| 'pausing'
	| 'paused'
	| 'interrupted'
	| 'finishing'
	| 'cancelling';

export type StreamingFailure =
	| 'microphone'
	| 'microphoneLost'
	| 'audio'
	| 'tokenRefused'
	| 'protocol'
	| 'transcriptChanged'
	| 'summary'
	| 'saveFailed'
	| 'unavailable'
	| 'cancelFailed';

export type StartResult = 'started' | 'busy' | 'otherTab' | 'microphone' | 'audio';

export type StreamingTarget = {
	targetID: string;
	targetType: TranscriptionTargetType;
	label?: string;
	targetPath: string;
	onLinkTranscription?: (transcriptionID: string) => Promise<void>;
	onUnlinkTranscription?: (transcriptionID: string) => Promise<void>;
	onRefresh?: () => void;
};

export type StreamingSessionInfo = {
	targetID: string;
	targetType: TranscriptionTargetType;
	label?: string;
	targetPath: string;
	transcriptionID: string | null;
};

export type StreamingState = {
	session: StreamingSessionInfo | null;
	phase: StreamingPhase;
	assemblyStatus: AssemblyStatus;
	syncStatus: SyncStatus;
	failure: StreamingFailure | null;
	language: TranscriptionLanguage | null;
	countdown: number | null;
	elapsed: number;
	/** The most recent waveform bars, at most MAX_BARS. */
	bars: number[];
	/** Bars recorded in total, so the waveform keeps scrolling after the cap. */
	barCount: number;
	current: number;
	/** Saved transcript plus unsaved chunks, once the server transcript is known. */
	transcript: string | null;
	/** Finished turns not yet saved to the server. */
	unsavedChunks: QueuedChunk[];
	partial: string;
	/** No speech has been picked up for a few seconds while recording. */
	silent: boolean;
	pendingChunks: number;
};

const LIVE_PHASES: StreamingPhase[] = ['starting', 'countdown', 'recording'];
const BUSY_PHASES: StreamingPhase[] = [...LIVE_PHASES, 'pausing', 'finishing', 'cancelling'];

const initialState: StreamingState = {
	session: null,
	phase: 'idle',
	assemblyStatus: 'idle',
	syncStatus: 'idle',
	failure: null,
	language: null,
	countdown: null,
	elapsed: 0,
	bars: [],
	barCount: 0,
	current: 0,
	transcript: null,
	unsavedChunks: [],
	partial: '',
	silent: false,
	pendingChunks: 0,
};

type TurnMessage = {
	type: 'Turn';
	turn_order: number;
	end_of_turn: boolean;
	turn_is_formatted: boolean;
	transcript: string;
	speaker_label?: string;
	words?: { speaker?: string }[];
};

type SpeakerRevisionMessage = {
	type: 'SpeakerRevision';
	revisions: { turn_order: number; speaker_label?: string }[];
};

type StreamingMessage =
	| { type: 'Begin'; id: string; expires_at: number }
	| TurnMessage
	| SpeakerRevisionMessage
	| { type: 'Termination' }
	| { type: 'Error'; error: string; error_code?: number };

type FinishedTurn = {
	order: number;
	label?: string;
	revisedLabel?: string;
	text: string;
	line: string;
};

type Connection = {
	sessionID: string;
	ws: WebSocket;
	began: boolean;
	closed: boolean;
	terminating: boolean;
	terminatedCleanly: boolean;
	terminated: Promise<void>;
	resolveTerminated: () => void;
	speakers: Map<string, number>;
	finishedOrders: Set<number>;
	turns: FinishedTurn[];
	revised: boolean;
	partial: string;
	queuedFirst: boolean;
	/** Chunks held back while the previous session is still ending. */
	held: QueuedChunk[] | null;
};

type PendingRevision = { sessionID: string; original: string; revised: string };

type AppendRequest =
	| {
			kind: 'chunks';
			chunks: QueuedChunk[];
			text: string;
			newSession: boolean;
			sessionID: string;
	  }
	| { kind: 'revision'; revision: PendingRevision; offset: number; text: string };

function createID(): string {
	return typeof crypto !== 'undefined' && 'randomUUID' in crypto
		? crypto.randomUUID()
		: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function retryDelay(attempt: number): number {
	return RETRY_DELAYS_MS[attempt] ?? STEADY_RETRY_DELAY_MS;
}

/**
 * True when a request never got a GraphQL answer, for example offline or
 * while the API is unreachable. Those are retried; anything else is an
 * answer from the server that a retry will not change.
 * @param error The rejection from an Apollo mutation.
 */
export function isNetworkError(error: unknown): boolean {
	return error instanceof ApolloError && !!error.networkError && !error.graphQLErrors.length;
}

/**
 * AssemblyAI uses these instead of a letter when it cannot attribute a turn
 * or word: `PENDING` for short turns and backchannels, `UNKNOWN` for errors.
 * Neither is a speaker.
 * @param label A `speaker_label` or word-level `speaker` from a Turn message.
 */
function isSpeaker(label: string | undefined): label is string {
	return !!label && label !== 'UNKNOWN' && label !== 'PENDING';
}

function speakerNumber(speakers: Map<string, number>, label?: string): number | undefined {
	if (!isSpeaker(label)) return undefined;
	// The server caps labels at MAX_SPEAKERS; if one slips through anyway,
	// an unattributed line beats inventing a third speaker.
	if (!speakers.has(label) && speakers.size >= Number(MAX_SPEAKERS)) return undefined;
	if (!speakers.has(label)) speakers.set(label, speakers.size + 1);
	return speakers.get(label);
}

function formatLine(speaker: number | undefined, text: string): string {
	return speaker ? `Speaker ${speaker}: ${text}` : text;
}

function turnLabel(turn: TurnMessage): string | undefined {
	if (isSpeaker(turn.speaker_label)) return turn.speaker_label;

	const counts = new Map<string, number>();
	for (const word of turn.words ?? []) {
		if (isSpeaker(word.speaker)) counts.set(word.speaker, (counts.get(word.speaker) ?? 0) + 1);
	}

	let best: string | undefined;
	for (const [label, count] of counts) {
		if (!best || count > (counts.get(best) ?? 0)) best = label;
	}

	return best;
}

/**
 * Joins unsaved streaming chunks onto a saved transcript the same way
 * `appendTranscript` does on the server.
 * @param transcript The saved transcript before `chunks`.
 * @param chunks Unsaved chunks in send order.
 */
export function appendQueuedChunks(transcript: string, chunks: readonly QueuedChunk[]): string {
	let text = transcript;

	for (const chunk of chunks) {
		if (!text) {
			text = chunk.text;
		} else if (chunk.newSession) {
			text = `${text}\n${TRANSCRIPT_BREAK}\n${chunk.text}`;
		} else {
			text = `${text}\n${chunk.text}`;
		}
	}

	return text;
}

/**
 * Consecutive chunks of one session, sent as a single append.
 * @param queue Chunks waiting to be appended, in send order.
 */
function takeBatch(queue: QueuedChunk[]): QueuedChunk[] {
	const first = queue[0];
	if (!first) return [];

	const batch = [first];
	for (let i = 1; i < queue.length && batch.length < MAX_BATCH_CHUNKS; i++) {
		if (queue[i].sessionID !== first.sessionID || queue[i].newSession) break;
		batch.push(queue[i]);
	}

	return batch;
}

function sleep(ms: number) {
	return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

async function isLockHeld(targetID: string): Promise<boolean> {
	if (!navigator.locks) return false;

	try {
		const { held } = await navigator.locks.query();
		return !!held?.some((lock) => lock.name === `${LOCK_PREFIX}${targetID}`);
	} catch {
		return false;
	}
}

/**
 * Appends a stored queue that no longer belongs to the active session, for
 * example one left behind by a closed tab.
 * @param client Apollo client used to append the leftover chunks.
 * @param stored A persisted session that no longer matches the active one.
 */
async function flushDetached(client: ApolloClient<object>, stored: StoredSession): Promise<void> {
	const { transcriptionID } = stored;
	if (!transcriptionID) {
		removeStoredSession(stored.targetID);
		return;
	}

	const queue = [...stored.queue];
	let expectedLength = stored.expectedLength;

	while (queue.length) {
		const batch = takeBatch(queue);
		const text = batch.map((chunk) => chunk.text).join('\n');

		try {
			const result = await client.mutate<
				AppendTranscriptionMutation,
				AppendTranscriptionMutationVariables
			>({
				mutation: AppendTranscriptionDocument,
				variables: {
					input: { transcriptionID, text, expectedLength, newSession: batch[0].newSession },
				},
			});
			const data = result.data?.appendTranscription;
			if (!data) throw new Error('Empty append response');

			const transcript = data.transcription.transcript ?? '';

			if (data.applied || transcript.endsWith(text) || transcript.length < expectedLength) {
				queue.splice(0, batch.length);
			}

			expectedLength = transcript.length;
		} catch {
			saveStoredSession({ ...stored, queue, expectedLength, state: 'paused' });
			return;
		}
	}

	removeStoredSession(stored.targetID);
}

/**
 * One streaming recorder for the whole app. It owns the microphone, the
 * AssemblyAI socket, and the text queue. React reads its state through
 * `useSyncExternalStore`.
 */
export class StreamingTranscriptionController {
	private state: StreamingState = initialState;
	private readonly listeners = new Set<() => void>();

	private target: StreamingTarget | null = null;
	private needsLink = false;
	/** The session started without a transcription, so Cancel unlinks it again. */
	private createdThisSession = false;
	/** Transcript length before this session; Cancel cuts the transcript back to it. */
	private sessionBaseline: number | null = null;

	private stream: MediaStream | null = null;
	private audio: AudioSource | null = null;
	private connection: Connection | null = null;
	private retiring: Connection | null = null;
	private connectGeneration = 0;

	private queue: QueuedChunk[] = [];
	private queueGeneration = 0;
	private expectedLength = 0;
	private lastTranscript: string | null = null;
	private readonly sessionOffsets = new Map<string, number>();
	private pendingRevisions: PendingRevision[] = [];
	private flushing: Promise<void> | null = null;

	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private reconnectAttempt = 0;
	/** Server errors in a row while starting a session; reset on Begin. */
	private startServerErrors = 0;
	private authRetried = false;
	private flushTimer: ReturnType<typeof setTimeout> | null = null;
	private flushAttempt = 0;
	/** Server errors in a row while saving text; reset on a successful save. */
	private flushServerErrors = 0;
	/** Invalidates a finish() loop when the user stops it or starts another. */
	private finishGeneration = 0;
	private countdownTimer: ReturnType<typeof setTimeout> | null = null;
	private tickTimer: ReturnType<typeof setInterval> | null = null;
	private rotationTimer: ReturnType<typeof setTimeout> | null = null;
	private levelPeak = 0;
	/** Seconds in a row with the input peak below SILENCE_LEVEL. */
	private quietSeconds = 0;

	private releaseLock: (() => void) | null = null;
	private wakeLock: WakeLockSentinel | null = null;
	private restored = false;

	public constructor(private readonly client: ApolloClient<object>) {}

	public getState = () => this.state;

	public subscribe = (listener: () => void) => {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	};

	public init(): () => void {
		window.addEventListener('online', this.handleOnline);
		window.addEventListener('offline', this.handleOffline);
		window.addEventListener('pagehide', this.handlePageHide);
		window.addEventListener('beforeunload', this.handleBeforeUnload);
		document.addEventListener('visibilitychange', this.handleVisibility);

		if (!this.restored) {
			this.restored = true;
			void this.restore();
		}

		return () => {
			window.removeEventListener('online', this.handleOnline);
			window.removeEventListener('offline', this.handleOffline);
			window.removeEventListener('pagehide', this.handlePageHide);
			window.removeEventListener('beforeunload', this.handleBeforeUnload);
			document.removeEventListener('visibilitychange', this.handleVisibility);
		};
	}

	/**
	 * Lets the page that shows the target provide its link and refresh callbacks.
	 * @param target Link and refresh callbacks for the page showing this recording.
	 */
	public bindTarget(target: StreamingTarget) {
		if (this.state.session?.targetID !== target.targetID) return;

		this.target = { ...this.target, ...target };
	}

	public isLive(): boolean {
		return BUSY_PHASES.includes(this.state.phase);
	}

	public async start(
		target: StreamingTarget,
		options: {
			language: TranscriptionLanguage;
			transcriptionID?: string | null;
		},
	): Promise<StartResult> {
		if (BUSY_PHASES.includes(this.state.phase)) return 'busy';

		const current = this.state.session;
		const sameTarget = current?.targetID === target.targetID;

		if (current && !sameTarget) this.close();

		if (!(await this.acquireLock(target.targetID))) return 'otherTab';

		const transcriptionID =
			(sameTarget ? current?.transcriptionID : null) ?? options.transcriptionID ?? null;
		const previousPhase = sameTarget ? this.state.phase : 'idle';
		/** Continue keeps the session; everything else starts a new one. */
		const continuing = sameTarget && !!current;

		this.target = { ...(sameTarget ? this.target : null), ...target };
		this.needsLink = !transcriptionID;
		this.reconnectAttempt = 0;
		this.startServerErrors = 0;
		this.authRetried = false;

		if (!continuing) {
			this.createdThisSession = !transcriptionID;
			this.sessionBaseline = null;
			this.lastTranscript = null;
			this.expectedLength = 0;
		}

		this.set({
			session: {
				targetID: target.targetID,
				targetType: target.targetType,
				label: target.label,
				targetPath: target.targetPath,
				transcriptionID,
			},
			phase: 'starting',
			failure: null,
			language: options.language,
			elapsed: continuing ? this.state.elapsed : 0,
			bars: [],
			barCount: 0,
			current: 0,
			partial: '',
			silent: false,
		});

		try {
			this.stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					channelCount: 1,
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true,
				},
			});
		} catch {
			this.abortStart(previousPhase, 'microphone');
			return 'microphone';
		}

		if (this.state.phase !== 'starting') {
			this.stopAudio();
			return 'busy';
		}

		this.stream.getAudioTracks()[0]?.addEventListener('ended', this.handleTrackEnded);

		try {
			this.audio = await createAudioSource(this.stream, {
				onLevel: this.handleLevel,
				onError: this.handleAudioError,
			});
		} catch (error) {
			Sentry.captureException(error);
			this.abortStart(previousPhase, 'audio');
			return 'audio';
		}

		if (this.state.phase !== 'starting') {
			this.stopAudio();
			return 'busy';
		}

		this.set({ phase: 'countdown', countdown: COUNTDOWN_SECONDS });
		this.persist();
		this.scheduleCountdown();
		void this.connect(false);

		return 'started';
	}

	public async resume(): Promise<StartResult> {
		const { session, language, phase } = this.state;

		if (!session || !language || (phase !== 'paused' && phase !== 'interrupted')) return 'busy';

		return this.start(this.target ?? session, {
			language,
			transcriptionID: session.transcriptionID,
		});
	}

	/**
	 * Only before a session has started. AssemblyAI transcribes one language
	 * per session, so a paused session keeps the language it began with.
	 * @param language The language the next session will be transcribed in.
	 */
	public setLanguage(language: TranscriptionLanguage) {
		if (this.state.phase !== 'idle') return;
		this.set({ language });
		this.persist();
	}

	public async pause(failure: StreamingFailure | null = null): Promise<void> {
		if (!LIVE_PHASES.includes(this.state.phase)) return;

		this.set({ phase: 'pausing', assemblyStatus: 'ending', countdown: null });
		this.connectGeneration++;
		this.clearTimer('countdownTimer');
		this.clearTimer('reconnectTimer');
		this.clearTimer('rotationTimer');
		this.stopTicking();
		this.audio?.setSink(null);

		if (this.retiring) await this.endConnection(this.retiring);
		if (this.connection) await this.endConnection(this.connection);

		this.connection = null;
		this.retiring = null;
		this.stopAudio();
		this.releaseWakeLock();
		this.releaseLock?.();

		this.set({
			phase: 'paused',
			assemblyStatus: failure && failure !== 'microphoneLost' ? 'failed' : 'closed',
			failure,
			partial: '',
			silent: false,
		});
		this.persist();

		await this.flush();
	}

	/** Waits for every queued turn to be saved, then starts the summary. */
	public async finish(): Promise<void> {
		if (LIVE_PHASES.includes(this.state.phase)) await this.pause();

		const { phase, session } = this.state;

		if (!session || (phase !== 'paused' && phase !== 'interrupted')) return;

		if (!session.transcriptionID) {
			this.close();
			return;
		}

		const generation = ++this.finishGeneration;
		this.flushServerErrors = 0;
		this.set({ phase: 'finishing', failure: null });

		const stopped = () => generation !== this.finishGeneration || this.state.phase !== 'finishing';

		while (this.queue.length || this.pendingRevisions.length) {
			await this.flush();

			if (stopped()) return;
			if (!this.queue.length && !this.pendingRevisions.length) break;

			if (this.flushServerErrors >= MAX_SERVER_ERRORS) {
				this.set({ phase: 'paused', failure: 'saveFailed' });
				this.persist();
				return;
			}

			// The failed save already scheduled a retry; this loop owns the timing now.
			this.clearTimer('flushTimer');
			await sleep(retryDelay(this.flushAttempt));

			if (stopped()) return;
		}

		if (this.lastTranscript !== '') {
			try {
				await this.client.mutate<
					RegenerateTranscriptionSummaryMutation,
					RegenerateTranscriptionSummaryMutationVariables
				>({
					mutation: RegenerateTranscriptionSummaryDocument,
					variables: { input: { transcriptionID: session.transcriptionID } },
				});
			} catch {
				if (!stopped()) this.set({ phase: 'paused', failure: 'summary' });
				return;
			}

			if (stopped()) {
				// Stopped while the summary was already being generated; show it anyway.
				this.target?.onRefresh?.();
				return;
			}
		}

		this.target?.onRefresh?.();
		this.close();
	}

	/** Leaves a finish that keeps failing. Unsaved text is kept for the next try. */
	public stopFinishing(): void {
		if (this.state.phase !== 'finishing') return;

		this.finishGeneration++;
		this.clearTimer('flushTimer');
		this.set({ phase: 'paused' });
		this.persist();
	}

	/**
	 * Stops before anything was said. A transcription that this run created is
	 * unlinked again, so the target does not keep an empty transcription.
	 */
	public async cancel(): Promise<void> {
		const createdThisSession = this.createdThisSession;
		const transcriptionID = this.state.session?.transcriptionID;
		const target = this.target;

		await this.pause();

		const nothingSaved = this.lastTranscript === '' && !this.queue.length;

		this.close();

		if (createdThisSession && nothingSaved && transcriptionID && target?.onUnlinkTranscription) {
			try {
				await target.onUnlinkTranscription(transcriptionID);
				target.onRefresh?.();
			} catch {
				// The empty transcription stays linked and can be removed from the page.
			}
		}
	}

	/**
	 * Throws away this session: unsaved text is dropped, and the transcript is
	 * cut back to what it was before the session started. A transcription the
	 * session created is unlinked instead. The summary was never touched, so
	 * it still matches the remaining text.
	 */
	public async discard(): Promise<void> {
		if (LIVE_PHASES.includes(this.state.phase)) await this.pause();

		const { phase, session } = this.state;

		if (!session || (phase !== 'paused' && phase !== 'interrupted')) return;

		const { transcriptionID } = session;
		const target = this.target;

		if (!transcriptionID) {
			this.close();
			return;
		}

		this.set({ phase: 'cancelling', failure: null });
		await this.discardQueue();

		if (this.state.phase !== 'cancelling') return;

		let removed: boolean;

		if (this.createdThisSession && target?.onUnlinkTranscription) {
			try {
				await target.onUnlinkTranscription(transcriptionID);
				removed = true;
			} catch {
				removed = false;
			}
		} else {
			removed = await this.truncateTranscript(transcriptionID, this.sessionBaseline ?? 0);
		}

		if (this.state.phase !== 'cancelling') return;

		if (!removed) {
			this.set({ phase: 'paused', failure: 'cancelFailed' });
			this.persist();
			return;
		}

		this.close();
		target?.onRefresh?.();
	}

	/**
	 * Cuts the stored transcript back to `baseline` characters. A stale length
	 * is corrected from the server's answer; network errors are retried.
	 * @param transcriptionID The transcription to cut.
	 * @param baseline Transcript length to keep, from before this session.
	 * @returns False after MAX_SERVER_ERRORS server errors in a row.
	 */
	private async truncateTranscript(transcriptionID: string, baseline: number): Promise<boolean> {
		let expectedLength = this.expectedLength;
		let serverErrors = 0;

		for (let attempt = 0; ; attempt++) {
			if (this.state.phase !== 'cancelling') return false;
			if (expectedLength <= baseline) return true;

			try {
				const result = await this.client.mutate<
					AppendTranscriptionMutation,
					AppendTranscriptionMutationVariables
				>({
					mutation: AppendTranscriptionDocument,
					variables: {
						input: {
							transcriptionID,
							text: '',
							expectedLength,
							newSession: false,
							replaceFrom: baseline,
						},
					},
				});
				const data = result.data?.appendTranscription;
				if (!data) throw new Error('Empty append response');

				const length = (data.transcription.transcript ?? '').length;
				if (data.applied || length <= baseline) return true;

				expectedLength = length;
				serverErrors = 0;
			} catch (error) {
				if (!isNetworkError(error) && ++serverErrors >= MAX_SERVER_ERRORS) return false;
			}

			await sleep(retryDelay(attempt));
		}
	}

	/**
	 * Drops the session from the recorder. Saved text stays on the transcription.
	 * @param options Close behavior options.
	 * @param options.discardQueue When true, drops unsaved queued chunks instead of flushing them.
	 */
	public close(options?: { discardQueue?: boolean }) {
		const { phase } = this.state;
		if (BUSY_PHASES.includes(phase) && phase !== 'finishing' && phase !== 'cancelling') return;

		const session = this.state.session;

		if (session) {
			if (!options?.discardQueue && this.queue.length && session.transcriptionID) {
				const stored = this.snapshot('paused');
				if (stored) {
					saveStoredSession(stored);
					void flushDetached(this.client, stored);
				}
			} else {
				removeStoredSession(session.targetID);
			}
		}

		this.queueGeneration++;
		this.queue = [];
		this.pendingRevisions = [];
		this.sessionOffsets.clear();
		this.lastTranscript = null;
		this.expectedLength = 0;
		this.target = null;
		this.clearTimer('flushTimer');
		this.releaseLock?.();
		this.state = { ...initialState };
		this.emit();
	}

	public dispose() {
		this.connectGeneration++;
		for (const connection of [this.connection, this.retiring]) {
			if (connection) this.sendTerminate(connection);
			if (connection) this.detach(connection);
		}
		this.connection = null;
		this.retiring = null;
		this.clearTimer('countdownTimer');
		this.clearTimer('reconnectTimer');
		this.clearTimer('rotationTimer');
		this.clearTimer('flushTimer');
		this.stopTicking();
		this.stopAudio();
		this.releaseWakeLock();
		this.releaseLock?.();
	}

	private set(patch: Partial<StreamingState>) {
		const held = this.connection?.held;
		const unsavedChunks = held?.length ? [...this.queue, ...held] : this.queue;
		const transcript =
			this.lastTranscript !== null ? appendQueuedChunks(this.lastTranscript, unsavedChunks) : null;

		this.state = {
			...this.state,
			...patch,
			transcript,
			unsavedChunks,
		};
		this.emit();
	}

	private emit() {
		for (const listener of this.listeners) listener();
	}

	private clearTimer(name: 'countdownTimer' | 'reconnectTimer' | 'rotationTimer' | 'flushTimer') {
		const timer = this[name];
		if (timer) clearTimeout(timer);
		this[name] = null;
	}

	private abortStart(previousPhase: StreamingPhase, failure: StreamingFailure) {
		this.stopAudio();
		this.releaseLock?.();

		if (previousPhase === 'idle') {
			this.set({ ...initialState, failure });
			this.target = null;
			return;
		}

		this.set({ phase: previousPhase, failure });
	}

	private snapshot(state: StoredSession['state']): StoredSession | null {
		const { session, language } = this.state;
		if (!session || !language) return null;

		return {
			targetID: session.targetID,
			targetType: session.targetType,
			targetPath: session.targetPath,
			label: session.label,
			transcriptionID: session.transcriptionID,
			language,
			state,
			elapsed: this.state.elapsed,
			expectedLength: this.expectedLength,
			sessionBaseline: this.sessionBaseline,
			createdThisSession: this.createdThisSession,
			queue: this.queue,
			updatedAt: Date.now(),
		};
	}

	private persist() {
		const { phase } = this.state;
		const live = BUSY_PHASES.includes(phase) || phase === 'interrupted';
		const stored = this.snapshot(live ? 'live' : 'paused');

		if (stored?.transcriptionID || (stored && live)) saveStoredSession(stored);
	}

	private async restore() {
		for (const stored of loadStoredSessions()) {
			if (!stored.transcriptionID) {
				removeStoredSession(stored.targetID);
				continue;
			}

			if (await isLockHeld(stored.targetID)) continue;

			if (stored.state === 'live' && this.state.phase === 'idle' && !this.state.session) {
				this.queue = stored.queue;
				this.expectedLength = stored.expectedLength;
				this.sessionBaseline = stored.sessionBaseline ?? null;
				this.createdThisSession = stored.createdThisSession ?? false;
				this.target = {
					targetID: stored.targetID,
					targetType: stored.targetType,
					targetPath: stored.targetPath,
					label: stored.label,
				};
				this.set({
					session: {
						targetID: stored.targetID,
						targetType: stored.targetType,
						targetPath: stored.targetPath,
						label: stored.label,
						transcriptionID: stored.transcriptionID,
					},
					phase: 'interrupted',
					language: stored.language,
					elapsed: stored.elapsed,
					pendingChunks: stored.queue.length,
				});
				void this.flush();
				continue;
			}

			void flushDetached(this.client, stored);
		}
	}

	private async discardQueue() {
		this.queueGeneration++;
		this.queue = [];
		this.pendingRevisions = [];
		this.sessionOffsets.clear();
		this.clearTimer('flushTimer');
		await this.flushing?.catch(() => {});
		this.set({ pendingChunks: 0, syncStatus: 'idle' });
	}

	private async acquireLock(targetID: string): Promise<boolean> {
		if (this.releaseLock) return true;
		if (!navigator.locks) return true;

		return new Promise<boolean>((resolve) => {
			navigator.locks
				.request(`${LOCK_PREFIX}${targetID}`, { ifAvailable: true }, (lock) => {
					if (!lock) {
						resolve(false);
						return;
					}

					resolve(true);

					return new Promise<void>((release) => {
						this.releaseLock = () => {
							this.releaseLock = null;
							release();
						};
					});
				})
				.catch(() => resolve(true));
		});
	}

	private scheduleCountdown() {
		this.countdownTimer = setTimeout(() => {
			this.countdownTimer = null;
			const next = (this.state.countdown ?? 1) - 1;

			if (next > 0) {
				this.set({ countdown: next });
				this.scheduleCountdown();
				return;
			}

			this.set({ phase: 'recording', countdown: null });
			this.startTicking();
			this.acquireWakeLock();
			this.updateSink();
			this.persist();
		}, 1_000);
	}

	private startTicking() {
		this.stopTicking();
		this.levelPeak = 0;
		this.quietSeconds = 0;
		this.tickTimer = setInterval(() => {
			const elapsed = this.state.elapsed + 1;
			this.quietSeconds = this.levelPeak < SILENCE_LEVEL ? this.quietSeconds + 1 : 0;
			this.set({
				elapsed,
				bars: [...this.state.bars, this.levelPeak].slice(-MAX_BARS),
				barCount: this.state.barCount + 1,
				silent: this.quietSeconds >= SILENCE_SECONDS,
			});
			this.levelPeak = 0;

			if (elapsed % PERSIST_EVERY_SECONDS === 0) this.persist();
		}, 1_000);
	}

	private stopTicking() {
		if (this.tickTimer) clearInterval(this.tickTimer);
		this.tickTimer = null;
	}

	private stopAudio() {
		this.audio?.stop();
		this.audio = null;

		if (this.stream) {
			this.stream.getAudioTracks()[0]?.removeEventListener('ended', this.handleTrackEnded);
			this.stream.getTracks().forEach((track) => track.stop());
		}

		this.stream = null;
	}

	private acquireWakeLock() {
		if (this.wakeLock || !('wakeLock' in navigator)) return;

		navigator.wakeLock
			.request('screen')
			.then((sentinel) => {
				if (this.state.phase !== 'recording') {
					void sentinel.release().catch(() => {});
					return;
				}

				this.wakeLock = sentinel;
				sentinel.addEventListener('release', () => {
					if (this.wakeLock === sentinel) this.wakeLock = null;
				});
			})
			.catch(() => {});
	}

	private releaseWakeLock() {
		void this.wakeLock?.release().catch(() => {});
		this.wakeLock = null;
	}

	private updateSink() {
		const sending =
			this.state.phase === 'recording' && (!!this.connection?.began || !!this.retiring?.began);

		this.audio?.setSink(sending ? this.sendFrame : null);
	}

	private sendFrame = (frame: ArrayBuffer) => {
		const connection = this.connection?.began ? this.connection : this.retiring;
		const ws = connection?.ws;

		if (!connection || !ws || ws.readyState !== WebSocket.OPEN) return;

		if (ws.bufferedAmount > MAX_BUFFERED_BYTES) {
			this.handleConnectionLost(connection, 1006);
			return;
		}

		ws.send(frame);
	};

	private async connect(reconnect: boolean): Promise<void> {
		const generation = ++this.connectGeneration;
		const session = this.state.session;
		const language = this.state.language;

		if (!session || !language) return;

		if (!this.retiring) this.set({ assemblyStatus: reconnect ? 'reconnecting' : 'connecting' });

		let data: StartStreamingTranscriptionMutation['startStreamingTranscription'] | undefined;
		let networkFailure = false;

		try {
			const result = await this.client.mutate<
				StartStreamingTranscriptionMutation,
				StartStreamingTranscriptionMutationVariables
			>({
				mutation: StartStreamingTranscriptionDocument,
				variables: {
					input: {
						targetID: session.targetID,
						targetType: session.targetType,
						language,
						transcriptionID: session.transcriptionID,
					},
				},
			});
			data = result.data?.startStreamingTranscription;
		} catch (error) {
			data = undefined;
			networkFailure = isNetworkError(error);
		}

		if (generation !== this.connectGeneration || !LIVE_PHASES.includes(this.state.phase)) return;

		if (!data) {
			this.handleStartFailure(networkFailure);
			return;
		}

		this.sessionBaseline ??= data.transcriptLength;

		if (!session.transcriptionID) {
			this.set({ session: { ...session, transcriptionID: data.transcription.id } });
		}

		this.persist();

		if (this.needsLink) {
			let linkNetworkFailure = false;

			try {
				await this.target?.onLinkTranscription?.(data.transcription.id);
				this.needsLink = false;
				this.target?.onRefresh?.();
			} catch (error) {
				linkNetworkFailure = isNetworkError(error);

				if (generation === this.connectGeneration && LIVE_PHASES.includes(this.state.phase)) {
					this.handleStartFailure(linkNetworkFailure);
				}

				return;
			}

			if (generation !== this.connectGeneration || !LIVE_PHASES.includes(this.state.phase)) return;
		}

		if (!this.flushing && !this.queue.length) {
			this.lastTranscript = data.transcription.transcript ?? '';
			this.expectedLength = data.transcriptLength;
		} else if (this.lastTranscript === null && data.transcriptLength === this.expectedLength) {
			this.lastTranscript = data.transcription.transcript ?? '';
		}

		this.openSocket(data);
		this.set({});
		void this.flush();
	}

	private openSocket(data: StartStreamingTranscriptionMutation['startStreamingTranscription']) {
		const language = this.state.language;
		const params = new URLSearchParams({
			token: data.token,
			speech_model: SPEECH_MODEL,
			speaker_labels: 'true',
			max_speakers: MAX_SPEAKERS,
			...(language && { language_codes: JSON.stringify([language]) }),
			...(this.audio?.encodingParams ?? {}),
		});

		if (data.prompt) params.set('prompt', data.prompt);
		if (data.keyterms.length) params.set('keyterms_prompt', JSON.stringify(data.keyterms));

		let resolveTerminated: () => void = () => {};
		const terminated = new Promise<void>((resolve) => {
			resolveTerminated = resolve;
		});

		const ws = new WebSocket(`${STREAMING_URL}?${params.toString()}`);
		ws.binaryType = 'arraybuffer';

		const connection: Connection = {
			sessionID: createID(),
			ws,
			began: false,
			closed: false,
			terminating: false,
			terminatedCleanly: false,
			terminated,
			resolveTerminated,
			speakers: new Map(),
			finishedOrders: new Set(),
			turns: [],
			revised: false,
			partial: '',
			queuedFirst: false,
			held: this.retiring ? [] : null,
		};

		ws.onmessage = (event) => this.handleMessage(connection, event);
		ws.onclose = (event) => this.handleClose(connection, event);

		this.connection = connection;
	}

	private handleMessage(connection: Connection, event: MessageEvent) {
		if (typeof event.data !== 'string') return;

		let message: StreamingMessage;
		try {
			message = JSON.parse(event.data) as StreamingMessage;
		} catch {
			return;
		}

		switch (message.type) {
			case 'Begin':
				this.handleBegin(connection);
				break;
			case 'Turn':
				this.handleTurn(connection, message);
				break;
			case 'SpeakerRevision':
				this.handleRevision(connection, message);
				break;
			case 'Termination':
				connection.terminatedCleanly = true;
				connection.resolveTerminated();
				break;
			case 'Error':
				Sentry.captureMessage(`AssemblyAI streaming error: ${message.error}`, {
					extra: { errorCode: message.error_code },
				});
				break;
		}
	}

	/**
	 * A network error keeps retrying with backoff: the `online` event retries
	 * at once. A server answer that keeps failing will not change, so after
	 * MAX_SERVER_ERRORS in a row the recorder stops instead of spinning.
	 * @param networkFailure Whether the request never reached the server.
	 */
	private handleStartFailure(networkFailure: boolean) {
		if (!networkFailure && ++this.startServerErrors >= MAX_SERVER_ERRORS) {
			void this.pause('unavailable');
			return;
		}

		this.scheduleReconnect();
	}

	private handleBegin(connection: Connection) {
		connection.began = true;

		if (connection !== this.connection) return;

		this.reconnectAttempt = 0;
		this.startServerErrors = 0;
		this.authRetried = false;
		this.set({ assemblyStatus: 'live' });
		this.updateSink();

		this.clearTimer('rotationTimer');
		this.rotationTimer = setTimeout(() => this.rotate(), SESSION_ROTATION_MS);

		const retiring = this.retiring;
		if (retiring) void this.endConnection(retiring);
	}

	private handleTurn(connection: Connection, turn: TurnMessage) {
		if (connection.finishedOrders.has(turn.turn_order)) return;

		if (!turn.end_of_turn) {
			connection.partial = turn.transcript;
			this.set({ partial: turn.transcript });
			return;
		}

		if (!turn.turn_is_formatted) return;

		connection.finishedOrders.add(turn.turn_order);
		connection.partial = '';

		const text = turn.transcript.trim();
		if (!text) {
			this.set({ partial: '' });
			return;
		}

		const label = turnLabel(turn);
		const line = formatLine(speakerNumber(connection.speakers, label), text);
		connection.turns.push({ order: turn.turn_order, label, text, line });

		const chunk: QueuedChunk = {
			id: createID(),
			sessionID: connection.sessionID,
			text: line,
			newSession: !connection.queuedFirst,
		};
		connection.queuedFirst = true;

		if (connection.held) {
			connection.held.push(chunk);
			this.set({ partial: '' });
		} else {
			this.enqueue([chunk], { partial: '' });
		}
	}

	private handleRevision(connection: Connection, message: SpeakerRevisionMessage) {
		for (const revision of message.revisions) {
			const turn = connection.turns.find((candidate) => candidate.order === revision.turn_order);

			if (
				turn &&
				revision.speaker_label &&
				revision.speaker_label !== (turn.revisedLabel ?? turn.label)
			) {
				turn.revisedLabel = revision.speaker_label;
				connection.revised = true;
			}
		}
	}

	private handleClose(connection: Connection, event: CloseEvent) {
		connection.closed = true;
		connection.resolveTerminated();

		if (connection.terminating) return;

		this.handleConnectionLost(connection, event.code);
	}

	private handleConnectionLost(connection: Connection, code: number) {
		this.detach(connection);
		this.flushPartial(connection);
		this.releaseHeld(connection);

		if (connection === this.retiring) {
			this.retiring = null;
			if (this.connection) this.releaseHeld(this.connection);
			this.updateSink();
			return;
		}

		if (connection !== this.connection) return;

		this.connection = null;
		this.clearTimer('rotationTimer');
		this.updateSink();

		if (!LIVE_PHASES.includes(this.state.phase)) return;

		if (code === 1008) {
			if (!this.authRetried) {
				this.authRetried = true;
				void this.connect(true);
				return;
			}

			void this.pause('tokenRefused');
			return;
		}

		if (code === 3006 || code === 3007) {
			Sentry.captureMessage(`AssemblyAI streaming closed with code ${code}`);
			void this.pause('protocol');
			return;
		}

		this.scheduleReconnect(code === 3009 ? BUSY_RETRY_DELAY_MS : undefined);
	}

	private scheduleReconnect(delay?: number) {
		if (this.reconnectTimer || !LIVE_PHASES.includes(this.state.phase)) return;

		const ms = delay ?? retryDelay(this.reconnectAttempt);
		this.reconnectAttempt++;
		this.set({ assemblyStatus: 'reconnecting' });

		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			void this.connect(true);
		}, ms);
	}

	/** Opens the next session first, so no speech is lost at the 3-hour limit. */
	private rotate() {
		this.rotationTimer = null;

		if (this.state.phase !== 'recording' || !this.connection?.began || this.retiring) return;

		this.retiring = this.connection;
		this.connection = null;
		void this.connect(false);
	}

	private sendTerminate(connection: Connection) {
		if (connection.ws.readyState !== WebSocket.OPEN) return;

		try {
			connection.ws.send(JSON.stringify({ type: 'Terminate' }));
		} catch {
			// The socket is already closing.
		}
	}

	/**
	 * Ends a session with `Terminate`, so AssemblyAI finishes the open turn.
	 * @param connection The AssemblyAI socket to finish with a Terminate message.
	 */
	private async endConnection(connection: Connection): Promise<void> {
		if (connection.terminating) return connection.terminated;

		connection.terminating = true;

		if (connection.began && !connection.closed) {
			this.sendTerminate(connection);
			await Promise.race([connection.terminated, sleep(TERMINATION_TIMEOUT_MS)]);
		}

		this.detach(connection);

		if (connection.terminatedCleanly) this.collectRevision(connection);
		else if (connection.partial.trim()) this.flushPartial(connection);

		this.releaseHeld(connection);

		if (this.retiring === connection) {
			this.retiring = null;
			if (this.connection) this.releaseHeld(this.connection);
			this.updateSink();
		}
	}

	private detach(connection: Connection) {
		connection.ws.onmessage = null;
		connection.ws.onclose = null;

		if (
			connection.ws.readyState === WebSocket.OPEN ||
			connection.ws.readyState === WebSocket.CONNECTING
		) {
			connection.ws.close(1000);
		}
	}

	private flushPartial(connection: Connection) {
		if (!connection.began) return;

		connection.partial = '';
		this.set({ partial: '' });
	}

	private releaseHeld(connection: Connection) {
		const held = connection.held;
		connection.held = null;

		if (held?.length) this.enqueue(held);
	}

	private collectRevision(connection: Connection) {
		if (!connection.revised) return;

		// Keep the numbers the live view used, so a revision that moves turns
		// between speakers does not also swap who is 1 and who is 2.
		const speakers = new Map(connection.speakers);
		const original = connection.turns.map((turn) => turn.line).join('\n');
		const revised = connection.turns
			.map((turn) =>
				formatLine(speakerNumber(speakers, turn.revisedLabel ?? turn.label), turn.text),
			)
			.join('\n');

		if (revised !== original) {
			this.pendingRevisions.push({ sessionID: connection.sessionID, original, revised });
		}
	}

	private enqueue(chunks: QueuedChunk[], patch?: Partial<StreamingState>) {
		this.queue = [...this.queue, ...chunks];
		this.set({ ...patch, pendingChunks: this.queue.length, syncStatus: 'pending' });
		this.persist();
		void this.flush();
	}

	private flush(): Promise<void> {
		if (!this.flushing) {
			this.flushing = this.runFlush().finally(() => {
				this.flushing = null;
			});
		}

		return this.flushing;
	}

	private scheduleFlush() {
		if (this.flushTimer) return;

		const ms = retryDelay(this.flushAttempt);
		this.flushAttempt++;
		this.flushTimer = setTimeout(() => {
			this.flushTimer = null;
			void this.flush();
		}, ms);
	}

	private nextAppend(): AppendRequest | null {
		const chunks = takeBatch(this.queue);

		if (chunks.length) {
			return {
				kind: 'chunks',
				chunks,
				text: chunks.map((chunk) => chunk.text).join('\n'),
				newSession: chunks[0].newSession,
				sessionID: chunks[0].sessionID,
			};
		}

		while (this.pendingRevisions.length) {
			const revision = this.pendingRevisions[0];
			const offset = this.sessionOffsets.get(revision.sessionID);
			const current = this.lastTranscript;

			if (
				offset === undefined ||
				current === null ||
				current.slice(offset, offset + revision.original.length) !== revision.original
			) {
				this.pendingRevisions.shift();
				continue;
			}

			return {
				kind: 'revision',
				revision,
				offset,
				text: revision.revised + current.slice(offset + revision.original.length),
			};
		}

		return null;
	}

	private async runFlush(): Promise<void> {
		const transcriptionID = this.state.session?.transcriptionID;
		const generation = this.queueGeneration;

		if (!transcriptionID) return;

		this.clearTimer('flushTimer');

		for (let request = this.nextAppend(); request; request = this.nextAppend()) {
			this.set({ syncStatus: 'syncing' });

			let data: AppendTranscriptionMutation['appendTranscription'] | undefined;
			let networkFailure = false;

			try {
				const result = await this.client.mutate<
					AppendTranscriptionMutation,
					AppendTranscriptionMutationVariables
				>({
					mutation: AppendTranscriptionDocument,
					variables: {
						input: {
							transcriptionID,
							text: request.text,
							expectedLength: this.expectedLength,
							newSession: request.kind === 'chunks' ? request.newSession : false,
							replaceFrom: request.kind === 'revision' ? request.offset : null,
						},
					},
				});
				data = result.data?.appendTranscription;
			} catch (error) {
				data = undefined;
				networkFailure = isNetworkError(error);
			}

			if (generation !== this.queueGeneration) return;

			if (!data) {
				if (!networkFailure && request.kind === 'revision') {
					// A speaker-label fix the server refuses is not worth blocking the
					// finish for; the summary maps speakers per part anyway.
					this.pendingRevisions.shift();
					continue;
				}

				if (!networkFailure) this.flushServerErrors++;

				this.set({ syncStatus: 'failed' });
				this.scheduleFlush();
				return;
			}

			this.flushAttempt = 0;
			this.flushServerErrors = 0;
			this.applyAppendResult(request, data);
		}

		this.set({
			syncStatus: this.queue.length ? 'pending' : 'synced',
			pendingChunks: this.queue.length,
		});
	}

	private applyAppendResult(
		request: AppendRequest,
		data: AppendTranscriptionMutation['appendTranscription'],
	) {
		const transcript = data.transcription.transcript ?? '';
		const previousLength = this.expectedLength;

		this.lastTranscript = transcript;
		this.expectedLength = transcript.length;

		if (request.kind === 'revision') {
			this.pendingRevisions.shift();

			if (data.applied) {
				const delta = request.revision.revised.length - request.revision.original.length;
				for (const [sessionID, offset] of this.sessionOffsets) {
					if (offset > request.offset) this.sessionOffsets.set(sessionID, offset + delta);
				}
			}

			return;
		}

		if (data.applied || transcript.endsWith(request.text)) {
			if (request.newSession) {
				this.sessionOffsets.set(request.sessionID, transcript.length - request.text.length);
			}

			this.queue = this.queue.slice(request.chunks.length);
		} else if (transcript.length < previousLength) {
			this.queue = [];
			this.set({ failure: 'transcriptChanged' });
		}

		this.set({ pendingChunks: this.queue.length });
		this.persist();
	}

	private handleLevel = (level: number) => {
		if (level > this.levelPeak) this.levelPeak = level;
		if (this.state.phase === 'recording') this.set({ current: level });
	};

	private handleAudioError = (error: Error) => {
		Sentry.captureException(error);
		void this.pause('audio');
	};

	private handleTrackEnded = () => {
		void this.pause('microphoneLost');
	};

	private handleOnline = () => {
		if (this.reconnectTimer) {
			this.clearTimer('reconnectTimer');
			void this.connect(true);
		}

		if (this.flushTimer || this.state.syncStatus === 'failed') {
			this.clearTimer('flushTimer');
			void this.flush();
		}
	};

	private handleOffline = () => {
		const connection = this.connection;
		if (connection) this.handleConnectionLost(connection, 1006);
	};

	private handlePageHide = () => {
		for (const connection of [this.connection, this.retiring]) {
			if (connection) this.sendTerminate(connection);
		}

		this.persist();
	};

	private handleBeforeUnload = (event: BeforeUnloadEvent) => {
		if (!LIVE_PHASES.includes(this.state.phase) && !this.queue.length) return;

		event.preventDefault();
		event.returnValue = '';
	};

	private handleVisibility = () => {
		if (document.visibilityState === 'visible' && this.state.phase === 'recording') {
			this.acquireWakeLock();
		}
	};
}

export function useStreamingTranscription() {
	const client = useApolloClient();
	const controllerRef = useRef<StreamingTranscriptionController | null>(null);

	if (!controllerRef.current) {
		controllerRef.current = new StreamingTranscriptionController(client);
	}

	const controller = controllerRef.current;

	useEffect(() => {
		const cleanup = controller.init();

		return () => {
			cleanup();
			controller.dispose();
		};
	}, [controller]);

	const state = useSyncExternalStore(controller.subscribe, controller.getState);

	return { state, controller };
}
